import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  GlobalStyles,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Brightness4Outlined from '@mui/icons-material/Brightness4Outlined';
import Brightness7Outlined from '@mui/icons-material/Brightness7Outlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import SettingsBrightnessOutlined from '@mui/icons-material/SettingsBrightnessOutlined';
import cwFavicon from '../../../assets/cw-favicon.svg';
import { useAppTheme } from '../../../contexts/ThemeContext';
import { GuideHero } from '../components/GuideHero';
import { GuidePrintDocument } from '../components/GuidePrintDocument';
import { GuideProgress } from '../components/GuideProgress';
import { GuideSection } from '../components/GuideSection';
import { GuideSidebar } from '../components/GuideSidebar';
import { GuideStep } from '../components/GuideStep';
import { BACKPACK_CONTENT } from '../content/backpackContent';
import { BRAVE_CONTENT } from '../content/braveContent';
import { OVERVIEW_CONTENT } from '../content/overviewContent';
import { NIGHTLY_CONTENT } from '../content/nightlyContent';
import { PHANTOM_CONTENT, type GuidePhase } from '../content/phantomContent';
import { SOLFLARE_CONTENT } from '../content/solflareContent';
import { WALLET_GUIDE_NAV, type GuideNavId } from '../content/walletNav';
import {
  GUIDE_ACCENT,
  guideContainedButtonSx,
  guideOutlineButtonSx,
  guideTokens,
} from '../theme/guideTokens';

type WalletGuideId = 'phantom' | 'nightly' | 'solflare' | 'backpack' | 'brave';

function isGuideNavId(value: string): value is GuideNavId {
  return WALLET_GUIDE_NAV.some((item) => item.id === value);
}

function isWalletGuideId(id: GuideNavId): id is WalletGuideId {
  return (
    id === 'phantom' ||
    id === 'nightly' ||
    id === 'solflare' ||
    id === 'backpack' ||
    id === 'brave'
  );
}

function stepsForWallet(id: WalletGuideId) {
  if (id === 'nightly') return NIGHTLY_CONTENT.steps;
  if (id === 'solflare') return SOLFLARE_CONTENT.steps;
  if (id === 'backpack') return BACKPACK_CONTENT.steps;
  if (id === 'brave') return BRAVE_CONTENT.steps;
  return PHANTOM_CONTENT.steps;
}

function walletGuideMeta(id: WalletGuideId) {
  if (id === 'nightly') return NIGHTLY_CONTENT;
  if (id === 'solflare') return SOLFLARE_CONTENT;
  if (id === 'backpack') return BACKPACK_CONTENT;
  if (id === 'brave') return BRAVE_CONTENT;
  return PHANTOM_CONTENT;
}

function firstStepIndexForPhase(walletId: WalletGuideId, phase: GuidePhase): number {
  return stepsForWallet(walletId).findIndex((step) => step.phase === phase);
}

export const WalletGuidePage: React.FC = () => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const { mode, setMode, isDark } = useAppTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const initialId = useMemo(() => {
    const hash = location.hash.replace('#', '');
    return isGuideNavId(hash) ? hash : 'overview';
  }, [location.hash]);

  const [activeId, setActiveId] = useState<GuideNavId>(initialId);
  const [activePhase, setActivePhase] = useState<GuidePhase>('install');

  useEffect(() => {
    setActiveId(initialId);
  }, [initialId]);

  const selectSection = useCallback(
    (id: GuideNavId) => {
      setActiveId(id);
      if (isWalletGuideId(id)) setActivePhase('install');
      navigate({ pathname: '/wallet-guide', hash: id }, { replace: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [navigate]
  );

  const jumpToPhase = useCallback(
    (phase: GuidePhase) => {
      if (!isWalletGuideId(activeId)) return;
      setActivePhase(phase);
      const index = firstStepIndexForPhase(activeId, phase);
      if (index < 0) return;
      const el = document.getElementById(`${activeId}-step-${index + 1}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [activeId]
  );

  useEffect(() => {
    if (!isWalletGuideId(activeId)) return;

    const observers: IntersectionObserver[] = [];
    stepsForWallet(activeId).forEach((step, index) => {
      const el = document.getElementById(`${activeId}-step-${index + 1}`);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActivePhase(step.phase);
        },
        { rootMargin: '-30% 0px -55% 0px', threshold: 0 }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [activeId]);

  const cycleTheme = () => {
    if (mode === 'system') setMode('dark');
    else if (mode === 'dark') setMode('light');
    else setMode('system');
  };

  const handleExportPdf = () => {
    window.print();
  };

  const themeIcon =
    mode === 'system' ? (
      <SettingsBrightnessOutlined fontSize="small" />
    ) : mode === 'dark' ? (
      <Brightness4Outlined fontSize="small" />
    ) : (
      <Brightness7Outlined fontSize="small" />
    );

  const themeLabel =
    mode === 'system' ? 'System theme' : mode === 'dark' ? 'Dark mode' : 'Light mode';

  return (
    <Box
      className="wallet-guide-screen"
      sx={{
        minHeight: '100dvh',
        color: 'text.primary',
        fontFamily: guideTokens.fontBody,
        background: isDark
          ? 'radial-gradient(1200px 500px at 10% -10%, rgba(232,52,28,0.12), transparent 55%), #000000'
          : 'radial-gradient(1200px 500px at 12% -8%, rgba(232,52,28,0.08), transparent 50%), linear-gradient(180deg, #F7F6F4 0%, #F3F1EE 100%)',
        '@media print': {
          background: '#fff',
          minHeight: 'auto',
        },
      }}
    >
      <GlobalStyles
        styles={{
          '@media print': {
            'body *': { visibility: 'hidden' },
            '#wallet-guide-print, #wallet-guide-print *': { visibility: 'visible' },
            '#wallet-guide-print': {
              position: 'absolute',
              left: 0,
              top: 0,
              width: '100%',
              padding: '16px 24px',
            },
            '@page': { margin: '14mm' },
          },
        }}
      />

      <GuidePrintDocument />

      <Box
        component="header"
        className="wallet-guide-header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: (t) =>
            t.palette.mode === 'dark' ? 'rgba(0,0,0,0.88)' : 'rgba(247,246,244,0.92)',
          backdropFilter: 'blur(12px)',
          boxShadow: `inset 0 -2px 0 ${GUIDE_ACCENT}`,
          '@media print': { display: 'none' },
        }}
      >
        <Container maxWidth="lg" sx={{ py: 1.25 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5}>
            <Stack direction="row" alignItems="center" gap={1.25} sx={{ minWidth: 0 }}>
              <Box
                component="img"
                src={cwFavicon}
                alt=""
                sx={{ width: 28, height: 28, flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: guideTokens.fontDisplay,
                    fontWeight: 800,
                    fontSize: '0.95rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em',
                  }}
                >
                  Wallet guide
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ fontFamily: guideTokens.fontBody }}
                >
                  Connect, send & receive CWY
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" alignItems="center" gap={0.75}>
              <Tooltip title={themeLabel}>
                <IconButton
                  size="small"
                  onClick={cycleTheme}
                  aria-label={`Theme: ${themeLabel}`}
                  sx={{ border: '1px solid', borderColor: 'divider' }}
                >
                  {themeIcon}
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                variant="text"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/profile/wallet')}
                sx={{
                  ...guideOutlineButtonSx,
                  border: 'none',
                  display: { xs: 'none', sm: 'inline-flex' },
                  color: 'text.secondary',
                }}
              >
                Back to wallet
              </Button>
              <IconButton
                size="small"
                onClick={() => navigate('/profile/wallet')}
                aria-label="Back to wallet"
                sx={{
                  display: { xs: 'inline-flex', sm: 'none' },
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container
        maxWidth="lg"
        sx={{ py: { xs: 2.5, md: 4 }, '@media print': { display: 'none' } }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '240px minmax(0, 1fr)' },
            gap: { xs: 1, md: 4 },
            alignItems: 'start',
          }}
        >
          {isMdUp ? (
            <GuideSidebar items={WALLET_GUIDE_NAV} activeId={activeId} onSelect={selectSection} />
          ) : (
            <GuideSidebar
              items={WALLET_GUIDE_NAV}
              activeId={activeId}
              onSelect={selectSection}
              variant="chips"
            />
          )}

          <Box
            component="article"
            sx={{
              position: 'relative',
              p: { xs: 2, sm: 3.5 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: (t) =>
                t.palette.mode === 'dark' ? 'rgba(18,18,18,0.95)' : 'rgba(255,255,255,0.88)',
              backdropFilter: 'blur(10px)',
              minHeight: 420,
              boxShadow: (t) =>
                t.palette.mode === 'dark' ? 'none' : '0 12px 40px rgba(15, 17, 17, 0.06)',
            }}
          >
            <Box
              sx={{
                position: 'sticky',
                top: { xs: 62, sm: 68 },
                zIndex: 6,
                display: 'flex',
                justifyContent: 'flex-end',
                mb: 1.5,
                py: 0.75,
                mx: { xs: -0.5, sm: -1 },
                px: { xs: 0.5, sm: 1 },
                bgcolor: (t) =>
                  t.palette.mode === 'dark' ? 'rgba(18,18,18,0.92)' : 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
              }}
            >
              <Tooltip title="Export guide as PDF">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PictureAsPdfOutlinedIcon />}
                  onClick={handleExportPdf}
                  aria-label="Export to PDF"
                  data-testid="wallet-guide-export-pdf"
                  sx={{
                    ...guideOutlineButtonSx,
                    borderColor: GUIDE_ACCENT,
                    color: GUIDE_ACCENT,
                    boxShadow: (t) =>
                      t.palette.mode === 'dark'
                        ? '0 4px 16px rgba(0,0,0,0.35)'
                        : '0 4px 14px rgba(15,17,17,0.08)',
                    '&:hover': {
                      borderColor: guideTokens.accentHover,
                      bgcolor: (t) => guideTokens.accentSoft(t.palette.mode === 'dark'),
                    },
                  }}
                >
                  Export PDF
                </Button>
              </Tooltip>
            </Box>

            {activeId === 'overview' && (
              <GuideSection id="overview" title={OVERVIEW_CONTENT.title} hideTitle>
                <GuideHero onStartPhantom={() => selectSection('phantom')} />

                <Stack spacing={3} sx={{ maxWidth: 720 }}>
                  {OVERVIEW_CONTENT.sections.map((section) => (
                    <Box key={section.heading}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: guideTokens.fontDisplay,
                          fontWeight: 700,
                          fontSize: '1.05rem',
                          letterSpacing: '-0.02em',
                          mb: 1,
                        }}
                      >
                        {section.heading}
                      </Typography>
                      <Stack spacing={1}>
                        {section.body.map((paragraph) => (
                          <Typography
                            key={paragraph.slice(0, 48)}
                            variant="body1"
                            color="text.secondary"
                            sx={{ lineHeight: 1.65, fontFamily: guideTokens.fontBody }}
                          >
                            {paragraph}
                          </Typography>
                        ))}
                      </Stack>
                      {'bullets' in section && section.bullets && (
                        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                          {section.bullets.map((bullet) => (
                            <Typography
                              component="li"
                              key={bullet.slice(0, 48)}
                              variant="body1"
                              color="text.secondary"
                              sx={{
                                mb: 0.75,
                                lineHeight: 1.6,
                                fontFamily: guideTokens.fontBody,
                              }}
                            >
                              {bullet}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>

                <Box sx={{ mt: 3.5, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5, fontFamily: guideTokens.fontBody }}
                  >
                    Ready to connect? Pick any supported wallet guide, then open your wallet page.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                    <Button
                      variant="contained"
                      onClick={() => selectSection('phantom')}
                      sx={guideContainedButtonSx}
                    >
                      Open Phantom guide
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => selectSection('nightly')}
                      sx={guideOutlineButtonSx}
                    >
                      Open Nightly guide
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => selectSection('solflare')}
                      sx={guideOutlineButtonSx}
                    >
                      Open Solflare guide
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => selectSection('backpack')}
                      sx={guideOutlineButtonSx}
                    >
                      Open Backpack guide
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => selectSection('brave')}
                      sx={guideOutlineButtonSx}
                    >
                      Open Brave guide
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/profile/wallet')}
                      sx={guideOutlineButtonSx}
                    >
                      Go to Wallet
                    </Button>
                  </Stack>
                </Box>
              </GuideSection>
            )}

            {isWalletGuideId(activeId) && (
              <GuideSection
                id={activeId}
                title={walletGuideMeta(activeId).title}
                lead={walletGuideMeta(activeId).lead}
              >
                <GuideProgress activePhase={activePhase} onSelectPhase={jumpToPhase} />

                <Box component="ol" sx={{ m: 0, p: 0 }}>
                  {stepsForWallet(activeId).map((step, index) => (
                    <GuideStep
                      key={step.title}
                      index={index + 1}
                      step={step}
                      stepId={`${activeId}-step-${index + 1}`}
                    />
                  ))}
                </Box>

                <Box
                  sx={{
                    mt: 1,
                    p: 2.5,
                    borderRadius: `${guideTokens.radiusCard}px`,
                    border: '1px solid',
                    borderColor: (t) => guideTokens.accentBorder(t.palette.mode === 'dark'),
                    bgcolor: (t) => guideTokens.accentSoft(t.palette.mode === 'dark'),
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: guideTokens.fontDisplay,
                      fontWeight: 800,
                      fontSize: '1rem',
                      mb: 0.75,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    Ready to connect
                    {activeId === 'nightly'
                      ? ' with Nightly'
                      : activeId === 'solflare'
                        ? ' with Solflare'
                        : activeId === 'backpack'
                          ? ' with Backpack'
                          : activeId === 'brave'
                            ? ' with Brave Wallet'
                            : ''}
                    ?
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, fontFamily: guideTokens.fontBody, lineHeight: 1.6 }}
                  >
                    {activeId === 'brave'
                      ? 'Use Brave browser, unlock Brave Wallet, choose it on CWY, approve Connect, then use Receive or Send. Keep a little SOL for network fees.'
                      : activeId === 'backpack'
                      ? 'Open your CWY Wallet page, choose Backpack, approve the signature request, then use Receive or Send. Keep a little SOL for network fees.'
                      : activeId === 'solflare'
                      ? 'Open My Crypto Wallet, choose Solflare, unlock the extension, then use Receive or Send. Keep a little SOL for network fees.'
                      : activeId === 'nightly'
                        ? 'Open your CWY Wallet page, choose Nightly, approve the signature request, then use Receive or Send. Keep a little SOL in Nightly for network fees.'
                        : 'Open your CWY Wallet page and click Connect wallet. Choose Phantom and approve the request in the extension.'}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/profile/wallet')}
                    sx={guideContainedButtonSx}
                  >
                    Connect wallet now
                  </Button>
                </Box>
              </GuideSection>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
